import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popup, NumberBox, SelectBox, TextBox } from "devextreme-react";
import { LoadPanel } from "devextreme-react/load-panel";
import axios from "axios";
import { alert } from "devextreme/ui/dialog";
import DynamicFromLookup from "../../../../../dynamicFormBuilder/DynamicFromLookup";
import { MESSAGES } from "../../../../../common/Messages";
import routes from "../../../../../assets/apiRoutes.json";
import { DynamicLookupOptions } from "../../../../../enum/Enum";
import "../../../../../dynamicFormBuilder/formField.css";

// Split Deposit dialog — port of Delphi uOMD0035 (OM Split Deposit).
// Transfers a deposit held on a parent sales order onto the current order.
const emptyGuid = "00000000-0000-0000-0000-000000000000";
const baseUrl = () => window._env_?.REACT_APP_APIS || "";
const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

// Basis for the Amount <-> Percent conversion (Delphi edtBasis Codes 'P'/'C').
const BASIS = { PARENT: "P", CURRENT: "C" };
const BASIS_OPTIONS = [
    { id: BASIS.PARENT, name: "Based on Parent Avl Deposit" },
    { id: BASIS.CURRENT, name: "Based on Current Trans Total" },
];

const CURRENCY_FORMAT = "#,##0.00";

// Parent-order picker: standard TransactionHeader lookup (dropdown + grid popup) restricted to orders holding a deposit.
const PARENT_ORDER_LOOKUP_LAYOUT = {
    valueExpr: "RowId",
    displayExpr: "TransactionNumber",
    sort: [{ selector: "TransactionNumber", desc: false }],
    searchEnabled: true,
    acceptCustomValue: false,
    gridKey: "B0C7F1A2-3D45-4E6F-8A90-1B2C3D4E5F60",
    params: { module: "OM", lookupType: "TransactionHeader", depositOnly: true, showLookupGrid: true, isPascalCase: true },
    columns: [
        { dataField: "TransactionNumber", caption: "Order #", dataType: "string" },
        { dataField: "CustomerIdentityId", caption: "Customer", dataType: "string" },
        { dataField: "CustomerName", caption: "Customer Name", dataType: "string" },
        { dataField: "ShipToId", caption: "Ship To", dataType: "string" },
        { dataField: "ProfileCode", caption: "Profile", dataType: "string" },
    ],
};

// Receipt-method picker: standard AC AcReceiptMethodSetups lookup (dropdown + grid popup).
const RECEIPT_METHOD_LOOKUP_LAYOUT = {
    valueExpr: "id",
    displayExpr: "receiptmethod",
    sort: [{ selector: "receiptmethod", desc: false }],
    searchEnabled: true,
    acceptCustomValue: false,
    gridKey: "C1D8E2B3-4E56-4F70-9B01-2C3D4E5F6071",
    params: { module: "AC", lookupType: "AcReceiptMethodSetups", showLookupGrid: true },
    columns: [
        { dataField: "receiptmethod", caption: "Receipt Method", dataType: "string" },
        { dataField: "description", caption: "Description", dataType: "string" },
    ],
};

function SplitDepositPopup(props) {
    const { showPopup = {}, setShowPopup, accessToken, onSuccess } = props;
    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;

    const [loading, setLoading] = useState(false);
    const [current, setCurrent] = useState({});
    const [parent, setParent] = useState(null);
    const [parentLocked, setParentLocked] = useState(false);
    const [receiptMethodRowId, setReceiptMethodRowId] = useState(null);
    const [basis, setBasis] = useState(BASIS.CURRENT);
    const [amount, setAmount] = useState(0);
    const [percent, setPercent] = useState(0);
    const [autoApply, setAutoApply] = useState(false);

    const parentLookupRef = useRef(null);
    const receiptLookupRef = useRef(null);

    const authHeaders = useCallback(() => ({
        access_token: accessToken,
        Authorization: `Bearer ${accessToken}`,
    }), [accessToken]);

    // Field descriptors for the shared DynamicFromLookup component (SelectBox display mode).
    const parentLookupField = useMemo(() => ({
        name: "parentHeaderRowId",
        label: "Order #",
        tableName: "OMTRANSACTIONHEADER",
        ignoredblocalization: true,
        isLabelChanged: true,
        required: false,
        readOnly: parentLocked,
        lookupDisplayMode: DynamicLookupOptions.SelectBoxDisplayMode,
        layout: PARENT_ORDER_LOOKUP_LAYOUT,
    }), [parentLocked]);

    const receiptLookupField = useMemo(() => ({
        name: "receiptMethodRowId",
        label: "Receipt Mthd",
        tableName: "ACRECEIPTMETHODSETUP",
        ignoredblocalization: true,
        isLabelChanged: true,
        required: false,
        readOnly: false,
        lookupDisplayMode: DynamicLookupOptions.SelectBoxDisplayMode,
        layout: RECEIPT_METHOD_LOOKUP_LAYOUT,
    }), []);

    const resetState = useCallback(() => {
        setCurrent({});
        setParent(null);
        setParentLocked(false);
        setReceiptMethodRowId(null);
        setBasis("C");
        setAmount(0);
        setPercent(0);
        setAutoApply(false);
    }, []);

    const closePopup = useCallback(() => {
        setShowPopup({ visible: false, headerRowId: null });
        resetState();
    }, [setShowPopup, resetState]);

    // Loads a parent-order snapshot via the same GetSplitDepositData method (Delphi GetParentData).
    // Note: changing the parent does not clear Amount/Percent (Delphi only clears them on Basis change).
    const selectParent = useCallback(async (parentRowId, lock = false) => {
        if (!parentRowId || parentRowId === emptyGuid) {
            setParent(null);
            setParentLocked(false);
            return;
        }
        if (parentRowId === current.headerRowId) {
            await alert("Order number parent is the same as the current order number - re-enter.", MESSAGES.ERROR);
            setParent(null);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`${baseUrl()}/${routes.OM.GetSplitDepositData}`, {
                params: { headerRowId: parentRowId },
                headers: authHeaders(),
            });
            const data = res.data || {};
            if (data.isCommitted) {
                await alert("Parent record has already been committed.", MESSAGES.ERROR);
                setParent(null);
                return;
            }
            if (!data.hasRemaining) {
                await alert("Parent deposit Available Amount is either zero or does not exist.", MESSAGES.ERROR);
                setParent(null);
                return;
            }
            setParent(data);
            setParentLocked(lock);
            if (!receiptMethodRowId && data.receiptMethodRowId) {
                setReceiptMethodRowId(data.receiptMethodRowId);
            }
        } catch (err) {
            await alert(err?.response?.data?.message || err?.message || "Error loading parent order.", MESSAGES.ERROR);
            setParent(null);
        } finally {
            setLoading(false);
        }
    }, [authHeaders, setLoading, receiptMethodRowId, current.headerRowId]);

    const loadData = useCallback(async () => {
        if (!visible || !headerRowId || headerRowId === emptyGuid) return;
        setLoading(true);
        try {
            const dataRes = await axios.get(`${baseUrl()}/${routes.OM.GetSplitDepositData}`, {
                params: { headerRowId },
                headers: authHeaders(),
            });

            const data = dataRes.data || {};
            setCurrent(data);
            setAutoApply(data.autoApply === true);
            setReceiptMethodRowId(data.receiptMethodRowId || null);

            // Order produced by a split links to a still-open parent — load and lock it (Delphi FormShow).
            if (data.parentHeaderRowId && data.parentHeaderRowId !== emptyGuid) {
                await selectParent(data.parentHeaderRowId, true);
            } else {
                setParent(null);
                setParentLocked(false);
            }
        } catch (err) {
            await alert(err?.response?.data?.message || err?.message || "Error loading Split Deposit data.", MESSAGES.ERROR);
            closePopup();
        } finally {
            setLoading(false);
        }
    }, [visible, headerRowId, authHeaders, setLoading, closePopup, selectParent]);

    useEffect(() => {
        if (visible) loadData();
    }, [visible, headerRowId]);

    // Basis for conversion: Current Trans Total or Parent Remaining (Delphi edtAmountTransferExit / edtPercentTransferExit).
    const conversionBase = basis === BASIS.CURRENT ? (current.transTotal || 0) : (parent?.remaining || 0);

    const onBasisChanged = useCallback((value) => {
        setBasis(value);
        setAmount(0);
        setPercent(0);
    }, []);

    // Recompute the paired field only for user edits (e.event set) — a programmatic value change
    // (from the paired setter) must not recompute back, or rounding would drift Amount/Percent.
    const onAmountChanged = useCallback((e) => {
        if (!e.event) return;
        const amt = Number(e.value) || 0;
        setAmount(amt);
        setPercent(conversionBase === 0 ? 0 : round2((amt / conversionBase) * 100));
    }, [conversionBase]);

    const onPercentChanged = useCallback((e) => {
        if (!e.event) return;
        const pct = Number(e.value) || 0;
        setPercent(pct);
        setAmount(round2((conversionBase * pct) / 100));
    }, [conversionBase]);

    // Mirror of Delphi ValidData.
    const validate = useCallback(async () => {
        if (!parent || !parent.headerRowId || parent.headerRowId === emptyGuid) {
            await alert("A Parent transaction must exist.", MESSAGES.ERROR);
            return false;
        }
        if (amount === 0) {
            await alert("Amount is required before transfer.", MESSAGES.ERROR);
            return false;
        }
        if (amount < 0) {
            await alert("Amount cannot be negative.", MESSAGES.ERROR);
            return false;
        }
        if (!receiptMethodRowId || receiptMethodRowId === emptyGuid) {
            await alert("Pay Method is required before transfer.", MESSAGES.ERROR);
            return false;
        }
        if (amount > (parent.remaining || 0)) {
            await alert("Amount Transfer cannot be greater than Parent Remaining.", MESSAGES.ERROR);
            return false;
        }
        if (percent > 100) {
            await alert("Percent Transfer cannot be greater than 100%.", MESSAGES.ERROR);
            return false;
        }
        if (percent < 0) {
            await alert("Percent Transfer cannot be negative.", MESSAGES.ERROR);
            return false;
        }
        const newBalanceDue = (current.transTotal || 0) - (amount + (current.remaining || 0));
        if (newBalanceDue < 0) {
            await alert(`Balance Due cannot be negative: ${newBalanceDue.toFixed(2)}.`, MESSAGES.ERROR);
            return false;
        }
        return true;
    }, [parent, amount, percent, receiptMethodRowId, current]);

    const handleSplit = useCallback(async () => {
        if (!(await validate())) return;

        setLoading(true);
        try {
            await axios.post(
                `${baseUrl()}/${routes.OM.ExecuteSplitDeposit}`,
                {
                    headerRowId: current.headerRowId,
                    parentHeaderRowId: parent.headerRowId,
                    receiptMethodRowId,
                    amount,
                    autoApply,
                },
                { headers: { ...authHeaders(), "Content-Type": "application/json" } }
            );
            // Delphi btnSplitClick just closes with mrOK — no success message.
            closePopup();
            if (onSuccess) onSuccess();
        } catch (err) {
            await alert(err?.response?.data?.message || err?.message || "Error splitting deposit.", MESSAGES.ERROR);
        } finally {
            setLoading(false);
        }
    }, [validate, current, parent, receiptMethodRowId, amount, autoApply, authHeaders, setLoading, closePopup, onSuccess]);

    const groupBox = { flex: 1, borderRadius: 6, padding: 12 };
    const fieldStack = { display: "flex", flexDirection: "column", gap: 10 };

    const readOnlyNumber = (label, value) => (
        <NumberBox label={label} value={value ?? 0} readOnly format={CURRENCY_FORMAT} stylingMode="outlined" labelMode="static" />
    );

    // Split action + Auto Apply live on the popup header, right-aligned (Delphi Split / Auto Apply).
    const headerToolbarItems = [
        {
            location: "after",
            toolbar: "top",
            locateInMenu: "never",
            widget: "dxCheckBox",
            options: { text: "Auto Apply Cash Upon Split", value: autoApply, onValueChanged: (e) => setAutoApply(e.value) },
        },
        {
            location: "after",
            toolbar: "top",
            locateInMenu: "never",
            widget: "dxButton",
            options: { text: "Split", type: "default", stylingMode: "contained", onClick: handleSplit },
        },
    ];

    return (
        <Popup
            visible={visible}
            onHiding={closePopup}
            title="Split Deposit"
            width={760}
            height={540}
            showCloseButton
            dragEnabled
            resizeEnabled
            toolbarItems={headerToolbarItems}
            wrapperAttr={{ class: "split-deposit-popup" }}
        >
            <LoadPanel
                visible={loading}
                shading
                shadingColor="rgba(0,0,0,0.2)"
                showIndicator
                showPane
                hideOnOutsideClick={false}
                container=".split-deposit-popup .dx-overlay-content"
                position={{ my: "center", at: "center", of: ".split-deposit-popup .dx-overlay-content" }}
            />
            <div style={{ padding: "4px 8px" }}>
                {/* Parent / Current side by side (Delphi uOMD0035) */}
                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <fieldset className="col-fieldset" style={groupBox}>
                        <legend className="list-title">Parent</legend>
                        <div style={fieldStack}>
                            <DynamicFromLookup
                                fieldRef={parentLookupRef}
                                name={parentLookupField.name}
                                required={false}
                                field={parentLookupField}
                                formFields={{ parentHeaderRowId: parent?.headerRowId ?? "" }}
                                layout={parentLookupField.layout}
                                editMode={false}
                                onValueChanged={(e) => selectParent(e.value, false)}
                            />
                            {readOnlyNumber("Trans Total", parent?.transTotal)}
                            {readOnlyNumber("Avl Deposit", parent?.remaining)}
                            {readOnlyNumber("Balance", parent?.balanceDue)}
                            {readOnlyNumber("Percent", parent?.percent)}
                        </div>
                    </fieldset>

                    <fieldset className="col-fieldset" style={groupBox}>
                        <legend className="list-title">Current</legend>
                        <div style={fieldStack}>
                            <TextBox label="Order #" value={current.orderNo || ""} readOnly stylingMode="outlined" labelMode="static" />
                            {readOnlyNumber("Trans Total", current.transTotal)}
                            {readOnlyNumber("Avl Deposit", current.remaining)}
                            {readOnlyNumber("Balance", current.balanceDue)}
                            {readOnlyNumber("Percent", current.percent)}
                        </div>
                    </fieldset>
                </div>

                {/* Transfer */}
                <fieldset className="col-fieldset" style={{ borderRadius: 6, padding: 12 }}>
                    <legend className="list-title">Transfer</legend>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <SelectBox
                            label="Basis"
                            dataSource={BASIS_OPTIONS}
                            value={basis}
                            valueExpr="id"
                            displayExpr="name"
                            stylingMode="outlined"
                            labelMode="static"
                            onValueChanged={(e) => onBasisChanged(e.value)}
                        />
                        <NumberBox label="Amount" value={amount} format={CURRENCY_FORMAT} stylingMode="outlined" labelMode="static" onValueChanged={onAmountChanged} />
                        <NumberBox label="Percent" value={percent} format={CURRENCY_FORMAT} stylingMode="outlined" labelMode="static" onValueChanged={onPercentChanged} />
                        <DynamicFromLookup
                            fieldRef={receiptLookupRef}
                            name={receiptLookupField.name}
                            required={false}
                            field={receiptLookupField}
                            formFields={{ receiptMethodRowId: receiptMethodRowId ?? "" }}
                            layout={receiptLookupField.layout}
                            editMode={false}
                            onValueChanged={(e) => setReceiptMethodRowId(e.value)}
                        />
                    </div>
                </fieldset>
            </div>
        </Popup>
    );
}

export default SplitDepositPopup;
